<template>
    <div class="main-content">
        <PurchaseMultipleLicenseOptions
            v-if="!isUpgradeMode && !isGiftMode"
            class="mb-6 lg:mb-4"
            @move-to-next-section="focusFirstLicenseOption"
        />
        
        <div class="main-header">
            <div class="plan-section">
                <p>{{ isUpgradeMode ? 'Modify Plan' : 'Select Plan' }}</p>
            </div>
            <div class="h-11 mb-0 sm:mb-2 w-auto sm:w-auto sm:flex-shrink-0 relative">
                <PrimeTabs ref="primeTabsRef" class="h-9 w-auto sm:w-auto" :value="String(selectedTerm)" @update:value="onTabChange">
                    <PrimeTabList class="mobile-no-wrap-tabs w-auto sm:w-auto">
                        <PrimeTab 
                            v-for="tab in tabs" 
                            :key="tab.title" 
                            :value="String(tab.value)"
                            :class="{
                                'current-term-highlight': isUpgradeMode && license?.term === tab.value,
                                'upgrade-term-highlight': isUpgradeMode && license?.slug === selectedPlanType && selectedTerm === tab.value && license?.term !== tab.value,
                                'disabled-tab': isTabDisabled(tab.value)
                            }"
                            class="mobile-responsive-tab"
                            :disabled="isTabDisabled(tab.value)"
                            @mouseenter="handleTooltipShow(tab.value)"
                            @mouseleave="showTooltip = false"
                        >
                            {{ tab.title }}
                            <span v-if="isUpgradeMode && license?.term === tab.value" class="current-badge">Current</span>
                            <span v-if="isUpgradeMode && selectedTerm === tab.value && license?.term !== tab.value" class="upgrade-badge">New</span>
                            <!-- Show available licenses for selected plan type and timeframe -->
                            <span v-if="purchaseType === PurchaseType.GIFT && getAvailableLicensesForTerm(tab.value) > 0" class="available-badge">
                                {{ getAvailableLicensesForTerm(tab.value) }}
                            </span>
                        </PrimeTab>
                    </PrimeTabList>
                </PrimeTabs>
                
                <!-- Custom Tooltip -->
                <div v-if="showTooltip" class="custom-tooltip-container">
                    <div class="custom-tooltip-content">
                        {{ tooltipMessage }}
                    </div>
                </div>
            </div>
        </div>

        <PurchaseInformation v-if="selectedTerm === LicenseRenewalTerm.MONTHLY" />
        
        <div
            v-for="option in licenseOptions"
            :key="option.type"
            :tabindex="0"
            role="button"
            :aria-label="`Select ${LicenseTypeName[option.type]} license option`"
            @keydown.enter="selectedPlanType = option.type"
            @keydown.space.prevent="selectedPlanType = option.type"
            class="license-option-wrapper"
        >
            <LicenseOption v-bind="option" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { getDialogRef } from '~/composables/dynamic-dialog';
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';
import { usePersonStore } from '~/stores/person';
import { LicenseRenewalTerm, LicenseType, LicenseTypeName, PurchaseType } from '~/types/licenses/licenses';
import { ModalNames } from '~/types/modals';
import LicenseOption from './Licenses/LicenseOption.vue';
import PurchaseInformation from './PurchaseInformation.vue';
import PurchaseMultipleLicenseOptions from './PurchaseMultipleLicenseOptions.vue';

const tabs = [
    { title: 'Monthly', value: LicenseRenewalTerm.MONTHLY },
    { title: '1 Year', value: LicenseRenewalTerm.YEARLY },
    { title: '2 Years', value: LicenseRenewalTerm.TWO_YEARS },
    { title: '3 Years', value: LicenseRenewalTerm.THREE_YEARS },
];

const dialogRef = getDialogRef(ModalNames.PURCHASE_LICENSES);
const {selectedTerm, purchaseType, license, selectedPlanType} = storeToRefs(useSubscribeStore());
const personStore = usePersonStore();

const isUpgradeMode = computed(() => purchaseType.value === PurchaseType.UPGRADE);
const isGiftMode = computed(() => purchaseType.value === PurchaseType.GIFT);
const currentLicenseTypeName = ref(dialogRef.value.data?.license?.type);

const primeTabsRef = ref<any>(null);

// Check if only Signature is available (Pro upgrading to Signature)
const isOnlySignatureAvailable = computed(() => {
    return isUpgradeMode.value && currentLicenseTypeName.value === 'Pro';
});

// Tooltip state
const showTooltip = ref(false);
const tooltipMessage = ref('');

// Check if a tab should be disabled
const isTabDisabled = (tabValue: LicenseRenewalTerm) => {
    // Signature licenses cannot be monthly
    if (tabValue === LicenseRenewalTerm.MONTHLY && isOnlySignatureAvailable.value) {
        return true;
    }
    
    // In upgrade mode, prevent term downgrades
    if (isUpgradeMode.value && license?.value?.term) {
        return tabValue < license.value.term;
    }
    
    return false;
};

// Handle tooltip display for disabled tabs
const handleTooltipShow = (tabValue: LicenseRenewalTerm) => {
    if (!isTabDisabled(tabValue)) {
        showTooltip.value = false;
        return;
    }
    
    showTooltip.value = true;
    
    if (tabValue === LicenseRenewalTerm.MONTHLY && isOnlySignatureAvailable.value) {
        tooltipMessage.value = 'Signature licenses are only available on a yearly or multi-year basis';
    } else if (isUpgradeMode.value && license?.value?.term && tabValue < license.value.term) {
        const termNames: Record<LicenseRenewalTerm, string> = {
            [LicenseRenewalTerm.MONTHLY]: 'monthly',
            [LicenseRenewalTerm.YEARLY]: 'yearly',
            [LicenseRenewalTerm.TWO_YEARS]: '2-year',
            [LicenseRenewalTerm.THREE_YEARS]: '3-year'
        };
        tooltipMessage.value = `Cannot downgrade from ${termNames[license.value.term]} to ${termNames[tabValue]} term`;
    }
};


const licenseOptionConfigs: Record<LicenseType, {
    type: LicenseType;
    description: string;
    unavailableMonthly?: boolean;
    upgradeNotice?: string;
}> = {
    [LicenseType.ESSENTIALS]: {
        type: LicenseType.ESSENTIALS,
        description: 'Automated data removal with a human touch',
    },
    [LicenseType.PRO]: {
        type: LicenseType.PRO,
        description: 'Comprehensive privacy across all sites and sources',
    },
    [LicenseType.SIGNATURE]: {
        type: LicenseType.SIGNATURE,
        description: 'White-glove expedited priority for individuals in crisis or at high risk',
        unavailableMonthly: true,
        upgradeNotice: 'Signature licenses are non-refundable',
    },
};

const licenseOptions = computed(() => {
  if (isUpgradeMode.value) {
    if (currentLicenseTypeName.value === 'Essentials') {
      selectedPlanType.value = LicenseType.PRO
      return [licenseOptionConfigs[LicenseType.PRO], licenseOptionConfigs[LicenseType.SIGNATURE]]
    }
    if (currentLicenseTypeName.value === 'Pro' || currentLicenseTypeName.value === 'Signature') {
      selectedPlanType.value = LicenseType.SIGNATURE
      return [licenseOptionConfigs[LicenseType.SIGNATURE]]
    }
  }
  selectedPlanType.value = LicenseType.PRO
  return [licenseOptionConfigs[LicenseType.ESSENTIALS], licenseOptionConfigs[LicenseType.PRO], licenseOptionConfigs[LicenseType.SIGNATURE]]
})


// Get available licenses for a specific plan type and term
const getAvailableLicensesForTerm = (term: LicenseRenewalTerm) => {
    if (!personStore.person?.licenses) return 0;
    // If no plan type is selected, show total available licenses across all plans for this term
    if (!selectedPlanType.value) {
        return personStore.person.licenses.filter(license => {
            return license.term === term && 
                   license.expired === false && 
                   !license.assigned_to;
        }).length;
    }
    
    // If plan type is selected, show available licenses for that specific plan and term
    const count= personStore.person.licenses.filter(license => {
        return license.term === term && 
               license.expired === false && 
               !license.assigned_to;
    }).length;

    return count;
};

//handle tab change
const onTabChange = (value: string | number) => {
    const numValue = Number(value);

    // Prevent switching to disabled tabs
    if (isTabDisabled(numValue)) {
        return;
    }

    selectedTerm.value = numValue;

    // If switching to Monthly and Signature is selected, unselect it and switch to Pro
    if (numValue === LicenseRenewalTerm.MONTHLY && selectedPlanType.value === LicenseType.SIGNATURE) {
        selectedPlanType.value = LicenseType.PRO;
    }
};


const focusFirstLicenseOption = () => {
    nextTick(() => {

        const tabElement = primeTabsRef.value?.$el?.querySelector('[role="tab"]:not([disabled])');
        if (tabElement) {
            tabElement.focus();
        }
    });
};
</script>

<style lang="scss" scoped>
.main-content {
    @apply w-full lg:max-w-2xl;
}

.main-header {
    @apply flex flex-row items-center justify-between gap-4 pb-6 lg:pb-2 sm:pb-3 self-stretch;
}

.plan-section {
    @apply flex items-center gap-2 flex-shrink-0;

    p {
        @apply text-zinc-900 font-inter text-xl font-semibold leading-8;
    }
}

.current-term-highlight {
    @apply bg-green-100 border-green-300;
}

.upgrade-term-highlight {
    @apply bg-blue-300 border-blue-300;
}

.current-badge {
    @apply ml-2 px-2 py-1 text-xs bg-green-500 text-white rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
}

.upgrade-badge {
    @apply ml-2 px-2 py-1 text-xs bg-blue-500 text-white rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
}

.available-badge {
    @apply ml-2 px-2 py-1 text-xs bg-violet-500 text-white rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
}

/* Mobile-only fixes for tab wrapping */
@media (max-width: 639px) {
    .main-header {
        /* Stack vertically on mobile so tabs go under "Modify Plan" */
        @apply flex-col items-start gap-2;
    }
    
    // .main-header > div:last-child {
    //     /* Tab container should only take needed width on mobile */
    //     width: auto !important;
    // }
    
    .main-header > div:last-child {
        /* Force PrimeTabs to take full width on mobile */
        :deep(.p-tabs) {
            width: 100% !important;
            display: flex !important;
            flex-grow: 1;
        }
        
        :deep(.p-tablist) {
            width: 100% !important;
            display: flex !important;
            flex-grow: 1;
        }
        
        /* Force individual tab buttons to stretch on mobile */
        :deep(.p-tab) {
            flex: 1 1 0px !important;
            min-width: 0 !important;
            width: auto !important;
        }
        
        :deep(.p-tab-header-content) {
            width: 100% !important;
            display: flex !important;
            justify-content: center !important;
        }
    }

    .mobile-no-wrap-tabs {
        @apply flex w-full;
        /* Tabs stretch to fill all available space on mobile */
        flex-direction: row !important;
        justify-content: stretch;
        gap: 4px;
        flex: 1 1 0px !important;
    }
    
    .mobile-responsive-tab {
        @apply px-2 py-1 text-xs text-center;
        /* Each tab takes equal space and stretches */
        flex: 1 1 0px !important;
        min-width: 0 !important;
        width: auto !important;
        
        /* Reduce badge sizes on mobile */
        .current-badge,
        .upgrade-badge,
        .available-badge {
            @apply ml-1 px-1 py-0.5 text-xs inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
        }
    }
}

/* Desktop - normal tab behavior */
@media (min-width: 640px) {
    .main-header {
        /* Keep Select Plan on left, tabs center-aligned and flex to right */
        @apply flex-row items-center gap-4;
    }
    
    .main-header > div:last-child {
        /* Tab container takes natural width, center-aligned with Select Plan */
        width: auto !important;
        flex-grow: 0;
        flex-shrink: 0;
        
        /* PrimeTabs take natural width */
        :deep(.p-tabs) {
            width: auto !important;
            display: flex !important;
        }
        
        :deep(.p-tablist) {
            width: auto !important;
            display: flex !important;
        }
        
        /* Individual tab buttons take natural width */
        :deep(.p-tab) {
            flex: 0 0 auto !important;
            min-width: auto !important;
            width: auto !important;
        }
        
        :deep(.p-tab-header-content) {
            width: auto !important;
            display: flex !important;
            justify-content: center !important;
        }
    }
    
    .mobile-no-wrap-tabs {
        /* Desktop tabs take natural width */
        width: auto !important;
        display: flex !important;
        flex: 0 0 auto !important;
        gap: 6px;
    }
    
    .mobile-responsive-tab {
        /* Tabs take natural width on desktop */
        @apply px-3 py-2 text-sm text-center;
        flex: 0 0 auto !important;
        min-width: auto !important;
        width: auto !important;
        
        /* Normal badge sizes on desktop */
        .current-badge,
        .upgrade-badge,
        .available-badge {
            @apply ml-2 px-2 py-1 text-xs inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
        }
    }
}

/* Custom Tooltip Styling */
.custom-tooltip-container {
    @apply absolute bottom-full left-1/2 mb-2;
    z-index: 9999;
    transform: translateX(-50%);
    
    /* Mobile positioning */
    @media (max-width: 639px) {
        left: 50%;
        transform: translateX(-50%);
    }
}

.custom-tooltip-content {
    @apply bg-zinc-900 text-white text-sm font-medium px-3 py-2 rounded-lg shadow-xl;
    @apply relative text-center;
    width: 280px;
    white-space: normal;
    line-height: 1.4;
    
    /* Mobile responsive sizing */
    @media (max-width: 639px) {
        @apply text-xs px-2 py-1;
        width: 240px;
    }
    
    /* Tooltip arrow pointing down */
    &::after {
        content: '';
        @apply absolute top-full left-1/2 transform -translate-x-1/2;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid #18181b;
        
        /* Smaller arrow on mobile */
        @media (max-width: 639px) {
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 6px solid #18181b;
        }
    }
}

/* Disabled tab styling */
.disabled-tab {
    @apply opacity-50 cursor-not-allowed;
    
    &:hover {
        @apply opacity-50;
    }
}

.license-option-wrapper {
    @apply outline-none;
    
    &:focus {
        @apply outline-2 outline-blue-500 outline-offset-2;
    }
    
    &:focus-visible {
        @apply outline-2 outline-blue-500 outline-offset-2;
    }
}
</style>
